import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { take } from 'rxjs';
import { CouponCodeModel, CouponModel } from '../../../models/coupon.model';
import { SeminarDateSeriesListModel } from '../../../models/seminar.model';
import { AlertService } from '../../../services/alert.service';
import { CouponService } from '../../../services/coupon.service';
import { SeminarService } from '../../../services/seminar.service';
import {
  fieldRequired,
  getMaxLengthFromValidator,
  getTextAreaLength,
} from '../../../utils/form.utils';
import { getSeminarFirstDateString } from '../../../utils/seminar.utils';
import { numberFormatValidator } from '../../../validators/number-format.validator';
import { CouponAlreadyExistsComponent } from '../coupon-already-exists/coupon-already-exists.component';

@Component({
  selector: 'app-coupon-formular',
  templateUrl: './coupon-formular.component.html',
  styleUrl: './coupon-formular.component.scss',
  standalone: false,
})
export class CouponFormularComponent implements OnInit {
  public couponType: 'sellable' | 'promo' = 'sellable';
  public couponForm: FormGroup;
  public seminars: SeminarDateSeriesListModel[] = [];
  public isLoadingSeminars = false;
  public isEditMode = false;
  public isLoadingCouponCode = false;
  private couponCodeId: number | null = null;
  private loadedCouponCode: CouponCodeModel | null = null;
  public conditionTypes = [
    { label: 'Regulär', value: 'regular' },
    { label: 'Frühbucher', value: 'early' },
    { label: 'BED Mitglied', value: 'bed' },
    { label: 'Praxis (ab 3 Personen)', value: 'office' },
    { label: 'LOGO Deutschland Mitglied', value: 'logo' },
    { label: 'VDB Mitglied', value: 'vdb' },
    { label: 'VDP Mitglied', value: 'vdp' },
  ];

  public fieldRequired = fieldRequired;
  public getTextAreaLength = getTextAreaLength;
  public getMaxLengthFromValidator = getMaxLengthFromValidator;
  public getSeminarDateString = getSeminarFirstDateString;

  constructor(
    private dialogRef: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private couponService: CouponService,
    private alertService: AlertService,
    private dialogService: DialogService,
    private seminarService: SeminarService
  ) {
    this.couponType = this.config.data.type;
    this.isEditMode = !!this.config.data.couponCodeId;
    this.couponCodeId = this.config.data.couponCodeId ?? null;
    this.createForm();
  }

  public ngOnInit(): void {
    if (this.couponType === 'promo') {
      this.loadSeminars();
      if (this.isEditMode && this.couponCodeId) {
        this.loadCouponCode(this.couponCodeId);
      }
    }
  }

  private loadCouponCode(couponCodeId: number): void {
    this.isLoadingCouponCode = true;
    this.couponService
      .getCouponCode(couponCodeId)
      .pipe(take(1))
      .subscribe({
        next: couponCode => {
          this.loadedCouponCode = couponCode;
          this.couponForm.patchValue({
            discountType: couponCode.discountType,
            value: couponCode.value,
            title: couponCode.title,
            description: couponCode.description,
            recipient: couponCode.recipient,
            code: couponCode.code,
            allowedSeminarIds: couponCode.allowedSeminarIds || [],
            allowedConditionTypes: couponCode.allowedConditionTypes || [],
            seminarInverted: couponCode.seminarInverted || false,
          });
          this.couponForm.get('code')?.disable();
          this.isLoadingCouponCode = false;
        },
        error: error => {
          console.error('Failed to load coupon code', error);
          this.alertService.showErrorAlert(
            'Beim Laden des Gutscheins ist ein Fehler aufgetreten'
          );
          this.isLoadingCouponCode = false;
          this.dialogRef.close(false);
        },
      });
  }

  private loadSeminars(): void {
    this.isLoadingSeminars = true;
    this.seminarService
      .getSeminars(true, false)
      .pipe(take(1))
      .subscribe({
        next: seminars => {
          this.seminars = seminars;
          this.isLoadingSeminars = false;
        },
        error: error => {
          console.error('Failed to load seminars', error);
          this.isLoadingSeminars = false;
        },
      });
  }

  private createForm(): void {
    if (this.couponType === 'sellable') {
      this.couponForm = new FormGroup({
        payedAmount: new FormControl(null, Validators.required),
        receivedAmount: new FormControl(null, Validators.required),
        title: new FormControl(null, Validators.required),
        description: new FormControl(null, Validators.maxLength(500)),
      });
      return;
    }
    this.couponForm = new FormGroup({
      discountType: new FormControl('fixed', Validators.required),
      value: new FormControl(null, [
        Validators.required,
        numberFormatValidator(),
      ]),
      title: new FormControl(null, Validators.required),
      description: new FormControl(null, Validators.maxLength(500)),
      recipient: new FormControl(null),
      code: new FormControl(null, Validators.required),
      allowedSeminarIds: new FormControl([]),
      allowedConditionTypes: new FormControl([]),
      seminarInverted: new FormControl(false),
    });
  }

  public closeDialog(confirmed: boolean) {
    if (!confirmed) {
      this.dialogRef.close(confirmed);
      return;
    }
    this.couponForm.markAllAsTouched();
    if (this.couponForm.invalid) {
      return;
    }

    if (this.couponType === 'sellable') {
      this.createSellableCoupon();
      return;
    }
    if (this.isEditMode) {
      this.updatePromoCode();
      return;
    }
    this.createPromoCode();
  }

  private createSellableCoupon(): void {
    const coupon: CouponModel = {
      ...this.couponForm.value,
    };
    this.couponForm.disable();
    this.couponService
      .createCoupon(coupon)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
        },
        error: error => {
          console.error('Failed to create coupon', error);
          if (error.error === 'invalid_amount') {
            this.alertService.showErrorAlert(
              'Der erhaltene Betrag muss größer als der bezahlte Betrag sein'
            );
          } else {
            this.alertService.showErrorAlert(
              'Beim Erstellen des Gutscheins ist ein Fehler aufgetreten'
            );
          }
          this.couponForm.enable();
        },
      });
  }

  private createPromoCode(): void {
    const formValue = this.couponForm.getRawValue();
    const couponCode: CouponCodeModel = {
      ...formValue,
      code: formValue.code.toUpperCase(),
      allowedSeminarIds: formValue.allowedSeminarIds || [],
      allowedConditionTypes: formValue.allowedConditionTypes || [],
      seminarInverted: formValue.seminarInverted || false,
    };
    this.couponForm.disable();
    this.couponService
      .createCouponCode(couponCode)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
        },
        error: error => {
          console.error('Failed to create coupon', error);
          if (error.error === 'code_already_exists') {
            this.dialogService.open(CouponAlreadyExistsComponent, {
              width: '475px',
              contentStyle: { overflow: 'auto' },
              breakpoints: {
                '640px': '90vw',
              },
              data: {
                couponCode: couponCode.code,
              },
            });
          } else {
            this.alertService.showErrorAlert(
              'Beim Erstellen des Gutscheins ist ein Fehler aufgetreten'
            );
          }
          this.couponForm.enable();
          if (this.isEditMode) {
            this.couponForm.get('code')?.disable();
          }
        },
      });
  }

  private updatePromoCode(): void {
    if (!this.loadedCouponCode) {
      return;
    }

    const formValue = this.couponForm.getRawValue();
    const couponCode: CouponCodeModel = {
      ...this.loadedCouponCode,
      ...formValue,
      allowedSeminarIds: formValue.allowedSeminarIds || [],
      allowedConditionTypes: formValue.allowedConditionTypes || [],
      seminarInverted: formValue.seminarInverted || false,
    };
    this.couponForm.disable();
    this.couponService
      .updateCouponCode(couponCode)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.dialogRef.close(true);
        },
        error: error => {
          console.error('Failed to update coupon', error);
          if (error.error === 'coupon_title_exists') {
            this.alertService.showErrorAlert(
              'Ein Gutschein mit diesem Titel existiert bereits'
            );
          } else {
            this.alertService.showErrorAlert(
              'Beim Speichern des Gutscheins ist ein Fehler aufgetreten'
            );
          }
          this.couponForm.enable();
          this.couponForm.get('code')?.disable();
        },
      });
  }
}
